<template>
  <NavbarHorizontalShell data_test="navbar-desktop-partner-topbar">
    <NavbarHorizontalGreeting v-if="is_dashboard_route" />

    <div
      v-else-if="show_tabs && mq.isDesktop"
      class="flex min-w-0 flex-1 items-center gap-4 self-stretch overflow-hidden"
    >
      <NavbarItemHorizontal
        v-for="item in routes_active"
        :key="item.name"
        class="shrink-0"
        :class="{ 'gap-2': item.prefix === ROUTES_PARTNERS.PROJECT_INFORMATION }"
        :item="item"
        :params="params"
      >
        <NavbarProjectTabContent
          v-if="item.prefix === ROUTES_PARTNERS.PROJECT_INFORMATION && current_project"
          :project="current_project"
        />
      </NavbarItemHorizontal>
    </div>

    <div v-else-if="show_tabs">
      <button
        type="button"
        class="cursor-pointer p-2 hover:text-gray-500"
        aria-haspopup="menu"
        @click="toggleBars"
      >
        <AppIcon
          name="regular-bars"
          size="lg"
        />
      </button>

      <PMenu
        ref="bars_menu_ref"
        :model="bars_menu_items"
        :popup="true"
      >
        <template #item="{ item, props: item_props }">
          <AppLink
            hide_underline
            :to="item.to"
            :data-test="item.data_test"
            v-bind="item_props.action"
          >
            <span>{{ item.label }}</span>
          </AppLink>
        </template>
      </PMenu>
    </div>

    <div
      v-else
      class="flex min-w-0 flex-1 items-center gap-4 self-stretch overflow-hidden"
    >
      <NavbarItemHorizontal
        class="shrink-0"
        :item="is_settings_profile_route ? profile_item : all_projects_item"
      />
    </div>
  </NavbarHorizontalShell>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMqService } from '@/plugins/mq';
import { mapGetters } from '@/store/lib/vue3helpers';
import {
  NAVBAR_ITEMS_PARTNERS_PROJECT,
  NAVBAR_ITEMS_PARTNERS_PROJECT_ACTIVE_ROLE_NEEDED,
} from '@/app/data/navbar_constants';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';
import NavbarItemHorizontal from '@/app/shared_components/navbar/NavbarItemHorizontal.vue';
import NavbarProjectTabContent from '@/app/_employees/components/navbar/NavbarProjectTabContent.vue';
import NavbarHorizontalShell from '@/app/shared_components/navbar/NavbarHorizontalShell.vue';
import NavbarHorizontalGreeting from '@/app/shared_components/navbar/NavbarHorizontalGreeting.vue';

const { t } = useI18n();
const route = useRoute();
const mq = useMqService();
const { proxy: app } = getCurrentInstance();

const { current_project, current_profile } = mapGetters(['current_project', 'current_profile']);

const bars_menu_ref = ref(null);

const is_dashboard_route = computed(() => route.name === ROUTES_PARTNERS.CLIENT_DASHBOARD);
const is_settings_profile_route = computed(() => route.name === ROUTES_PARTNERS.SETTINGS_PROFILE);

const show_tabs = computed(() => !!current_project.value);

const params = computed(() => {
  if (!current_project.value) return {};
  return { project_id: current_project.value.id };
});

function isFlipperEnabled(r) {
  return !r.flipper || app.$flipper.enabled(r.flipper);
}

function isRouteActive(r) {
  if (!isFlipperEnabled(r)) return false;
  if (NAVBAR_ITEMS_PARTNERS_PROJECT_ACTIVE_ROLE_NEEDED.includes(r.name)) {
    if (!current_project.value) return false;
    return current_profile.value.attributes.active_role_project_ids.includes(parseInt(current_project.value.id, 10));
  }
  return true;
}

const routes_active = computed(() => NAVBAR_ITEMS_PARTNERS_PROJECT.filter(isRouteActive));

const all_projects_item = {
  name: ROUTES_PARTNERS.OVERVIEW_ALL_PROJECTS,
  prefix: ROUTES_PARTNERS.OVERVIEW_ALL_PROJECTS,
  title: 'navbar.partner.projects',
  data_test: 'partner-navbar-all-projects-tab',
};

const profile_item = {
  name: ROUTES_PARTNERS.SETTINGS_PROFILE,
  prefix: ROUTES_PARTNERS.SETTINGS_PROFILE,
  title: 'navbar.partner.profile',
  data_test: 'partner-navbar-profile-tab',
};

const bars_menu_items = computed(() =>
  routes_active.value.map((item) => ({
    label: t(item.title),
    to: { name: item.name, params: params.value },
    data_test: item.data_test,
  })),
);

function toggleBars(event) {
  bars_menu_ref.value.toggle(event);
}
</script>
